import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import AudioFileRoundedIcon from "@mui/icons-material/AudioFileRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import KeyboardVoiceRoundedIcon from "@mui/icons-material/KeyboardVoiceRounded";
import { LanguageContext } from "../context/LanguageContext";
import { useAuth } from "../hooks/AuthProvider";
import { useAiVoiceAssistantSettings } from "../context/AiVoiceAssistantContext";
import { AI_VOICE_SESSION_TTL_MINUTES } from "../Constants";
import {
  processVoiceCommand,
  sendAssistantChatMessage,
} from "../services/aiVoiceAssistantApi";
import aiVoiceAssistantTranslations from "../languages/aiVoiceAssistant.json";
import "./AiVoiceAssistant.css";

const CONVERSATION_STORAGE_PREFIX = "sama-ai-voice-conversation";
const SESSION_TTL_MS = Math.max(1, Number(AI_VOICE_SESSION_TTL_MINUTES) || 15) * 60 * 1000;

const MIME_TYPE_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

const DEFAULT_TEXT = aiVoiceAssistantTranslations.en.aiVoiceAssistant;

const getPreferredMimeType = () => {
  if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
    return "";
  }

  return MIME_TYPE_CANDIDATES.find((type) => window.MediaRecorder.isTypeSupported(type)) || "";
};

const createId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const createUuid = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16);
    const value = char === "x" ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isUuid = (value) => typeof value === "string" && UUID_PATTERN.test(value);

const createSession = () => ({
  sessionId: createUuid(),
  sessionExpiresAt: Date.now() + SESSION_TTL_MS,
});

const isSessionExpired = (sessionExpiresAt) => {
  const expiryMs = Number(sessionExpiresAt);
  return !Number.isFinite(expiryMs) || expiryMs <= Date.now();
};

const getPreviewTitle = (source, label, text) => {
  if (source === "upload" && label) return label;
  if (source === "recording") return text.liveRecording;
  return "";
};

const formatDuration = (ms) => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const readAudioDuration = (url) =>
  new Promise((resolve) => {
    if (typeof window === "undefined" || !url) {
      resolve(0);
      return;
    }

    const audio = new window.Audio();

    const finish = (duration) => {
      audio.removeAttribute("src");
      audio.load();
      resolve(Number.isFinite(duration) ? Math.max(0, Math.round(duration * 1000)) : 0);
    };

    audio.preload = "metadata";
    audio.onloadedmetadata = () => finish(audio.duration);
    audio.onerror = () => finish(0);
    audio.src = url;
  });

const getConversationStorageKey = (userId, organizationId) => {
  const safeUserId = userId || "anonymous";
  const safeOrgId = organizationId || "no-organization";
  return `${CONVERSATION_STORAGE_PREFIX}:${safeUserId}:${safeOrgId}`;
};

const readConversation = (storageKey) => {
  if (typeof window === "undefined" || !storageKey) {
    return { key: storageKey, ...createSession(), messages: [] };
  }

  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) {
      return { key: storageKey, ...createSession(), messages: [] };
    }

    const parsed = JSON.parse(raw);
    const storedSession = {
      sessionId: parsed?.sessionId,
      sessionExpiresAt: parsed?.sessionExpiresAt,
    };
    const session =
      isUuid(storedSession.sessionId) && !isSessionExpired(storedSession.sessionExpiresAt)
        ? storedSession
        : createSession();

    return {
      key: storageKey,
      ...session,
      messages: Array.isArray(parsed?.messages) ? parsed.messages : [],
    };
  } catch (error) {
    console.error("Failed to read AI voice assistant conversation:", error);
    return { key: storageKey, ...createSession(), messages: [] };
  }
};

const getReadableError = (error, text = DEFAULT_TEXT) => {
  if (error?.name === "NotAllowedError" || error?.name === "SecurityError") {
    return text.microphoneBlocked;
  }

  const data = error?.response?.data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message.trim();
  if (typeof data?.error === "string" && data.error.trim()) return data.error.trim();
  if (typeof error?.message === "string" && error.message.trim()) return error.message.trim();

  return text.processFailed;
};

function MessageBubble({ message, text }) {
  const isUser = message.role === "user";
  const candidates = Array.isArray(message?.clarification?.candidates)
    ? message.clarification.candidates
    : [];
  const sources = Array.isArray(message.sources) ? message.sources : [];
  const readableSources = sources
    .map((source) => {
      if (typeof source === "string") return source.trim();
      if (!source || typeof source !== "object") return "";
      return String(source.title || source.name || source.source || source.url || source.id || "").trim();
    })
    .filter(Boolean);

  return (
    <div className={`ai-va-message-row ${isUser ? "is-user" : "is-assistant"}`}>
      <div className="ai-va-message-bubble">
        <div className="ai-va-message-meta">
          <span>{isUser ? text.userLabel : text.assistantLabel}</span>
          {message.duration ? <span>{formatDuration(message.duration)}</span> : null}
        </div>

        <p>{message.text}</p>

        {message.needsClarification ? (
          <>
            <span className="ai-va-message-chip">{text.clarification}</span>

            {candidates.length > 0 ? (
              <div className="ai-va-clarification-panel">
                <div className="ai-va-clarification-label">{text.candidates}</div>
                <div className="ai-va-clarification-list">
                  {candidates.map((candidate) => (
                    <span key={candidate} className="ai-va-clarification-candidate">
                      {candidate}
                    </span>
                  ))}
                </div>
                <p className="ai-va-clarification-hint">{text.clarificationHint}</p>
              </div>
            ) : null}
          </>
        ) : null}

        {message.audioSource ? (
          <audio className="ai-va-message-audio" controls src={message.audioSource} />
        ) : null}

        {readableSources.length > 0 ? (
          <div className="ai-va-message-sources">
            <span>{text.sources}</span>
            {readableSources.slice(0, 4).map((source, index) => (
              <span key={`${source}-${index}`}>{source}</span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function AiVoiceAssistant() {
  const { settings } = useAiVoiceAssistantSettings();
  const { org, permissions, user } = useAuth();
  const { lang } = useContext(LanguageContext);
  const text = useMemo(
    () => ({
      ...DEFAULT_TEXT,
      ...(aiVoiceAssistantTranslations[lang]?.aiVoiceAssistant || {}),
    }),
    [lang]
  );
  const isRtl = ["ar", "ur"].includes(lang);
  const canUseVoiceCommand = Boolean(permissions?.ai_voice_command);
  const canUseAssistantChat = Boolean(permissions?.ai_assistant_chat);
  const canUseAssistant = canUseVoiceCommand || canUseAssistantChat;
  const availableAssistantModes = useMemo(
    () => [
      ...(canUseVoiceCommand ? ["voice"] : []),
      ...(canUseAssistantChat ? ["chat"] : []),
    ],
    [canUseAssistantChat, canUseVoiceCommand]
  );
  const showModeTabs = availableAssistantModes.length > 1;

  const [open, setOpen] = useState(false);
  const [conversation, setConversation] = useState(() => ({
    key: "",
    ...createSession(),
    messages: [],
  }));
  const [recorderState, setRecorderState] = useState("ready");
  const [recordedMs, setRecordedMs] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [previewSource, setPreviewSource] = useState("");
  const [previewLabel, setPreviewLabel] = useState("");
  const [assistantMode, setAssistantMode] = useState("voice");
  const [chatInput, setChatInput] = useState("");
  const [chatState, setChatState] = useState("ready");
  const activeAssistantMode = availableAssistantModes.includes(assistantMode)
    ? assistantMode
    : availableAssistantModes[0] || "voice";

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const discardRecordingRef = useRef(false);
  const previewUrlRef = useRef("");
  const previewAudioRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const uploadAbortRef = useRef(null);
  const chatAbortRef = useRef(null);

  const organizationId = org?.id;
  const conversationKey = useMemo(
    () => getConversationStorageKey(user?.id, organizationId),
    [organizationId, user?.id]
  );

  useEffect(() => {
    setConversation(readConversation(conversationKey));
  }, [conversationKey]);

  useEffect(() => {
    if (conversation.key !== conversationKey || typeof window === "undefined") return;

    try {
      window.sessionStorage.setItem(
        conversationKey,
        JSON.stringify({
          sessionId: conversation.sessionId,
          sessionExpiresAt: conversation.sessionExpiresAt,
          messages: conversation.messages,
        })
      );
    } catch (error) {
      console.error("Failed to save AI voice assistant conversation:", error);
    }
  }, [conversation, conversationKey]);

  useEffect(() => {
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);

  const messages = useMemo(
    () => (conversation.key === conversationKey ? conversation.messages : []),
    [conversation.key, conversation.messages, conversationKey]
  );
  const sessionId = useMemo(
    () => (conversation.key === conversationKey ? conversation.sessionId : createSession().sessionId),
    [conversation.key, conversation.sessionId, conversationKey]
  );
  const hasPreview = Boolean(audioBlob && previewUrl);
  const isRecording = recorderState === "recording";
  const isProcessing = recorderState === "processing";
  const isChatProcessing = chatState === "processing";
  const position = settings.position || "bottom-right";
  const isLeftPosition = position.endsWith("left");
  const shellClassName = [
    "ai-va-shell",
    `is-${position}`,
    isLeftPosition ? "is-left" : "is-right",
    open ? "is-open" : "is-closed",
    isRtl ? "is-rtl" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const statusText = useMemo(() => {
    if (activeAssistantMode === "chat") {
      if (errorMessage && chatState === "error") return errorMessage;
      if (chatState === "processing") return text.chatThinking;
      return text.chatReady;
    }

    if (errorMessage && recorderState === "error") return errorMessage;
    if (recorderState === "recording") return text.recording;
    if (recorderState === "preview") {
      return previewSource === "upload" ? text.uploadReady : text.preview;
    }
    if (recorderState === "processing") return text.processing;
    return text.ready;
  }, [activeAssistantMode, chatState, errorMessage, previewSource, recorderState, text]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const clearPreview = useCallback(() => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = "";
    }

    setAudioBlob(null);
    setPreviewUrl("");
    setRecordedMs(0);
    setIsPreviewPlaying(false);
    setPreviewSource("");
    setPreviewLabel("");
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.currentTime = 0;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") return;
    recorder.stop();
  }, []);

  const cancelRecording = useCallback(() => {
    discardRecordingRef.current = true;
    setErrorMessage("");

    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
      return;
    }

    stopTimer();
    stopStream();
    clearPreview();
    setRecorderState("ready");
  }, [clearPreview, stopStream, stopTimer]);

  const updateConversation = useCallback(
    (updater) => {
      setConversation((prev) => {
        if (prev.key !== conversationKey) return prev;
        const next = typeof updater === "function" ? updater(prev) : updater;
        return { ...prev, ...next };
      });
    },
    [conversationKey]
  );

  const applyAudioPreview = useCallback(
    async (blob, nextSource, nextLabel = "") => {
      if (!blob || !blob.size) {
        setRecorderState("error");
        setErrorMessage(text.invalidAudioFile);
        return;
      }

      clearPreview();
      setErrorMessage("");

      const url = window.URL.createObjectURL(blob);
      const durationMs = await readAudioDuration(url);

      setAudioBlob(blob);
      setPreviewUrl(url);
      setPreviewSource(nextSource);
      setPreviewLabel(nextLabel);
      setRecordedMs(durationMs);
      setRecorderState("preview");
    },
    [clearPreview, text.invalidAudioFile]
  );

  const startRecording = useCallback(async () => {
    if (isProcessing) return;

    if (!window.navigator?.mediaDevices?.getUserMedia || typeof window.MediaRecorder === "undefined") {
      setRecorderState("error");
      setErrorMessage(text.noAudioSupport);
      return;
    }

    clearPreview();
    setErrorMessage("");
    discardRecordingRef.current = false;
    chunksRef.current = [];

    try {
      const stream = await window.navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const mimeType = getPreferredMimeType();
      const recorder = new window.MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      streamRef.current = stream;
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onerror = (event) => {
        setRecorderState("error");
        setErrorMessage(getReadableError(event.error, text));
      };

      recorder.onstop = () => {
        stopTimer();
        stopStream();
        recorderRef.current = null;

        const chunks = chunksRef.current;
        chunksRef.current = [];

        if (discardRecordingRef.current) {
          discardRecordingRef.current = false;
          clearPreview();
          setRecorderState("ready");
          return;
        }

        const blob = new Blob(chunks, {
          type: recorder.mimeType || mimeType || "audio/webm",
        });

        if (!blob.size) {
          setRecorderState("error");
          setErrorMessage(text.recordingEmpty);
          return;
        }

        applyAudioPreview(blob, "recording");
      };

      startedAtRef.current = Date.now();
      setRecordedMs(0);
      recorder.start(250);
      setRecorderState("recording");

      timerRef.current = window.setInterval(() => {
        setRecordedMs(Date.now() - startedAtRef.current);
      }, 250);
    } catch (error) {
      stopTimer();
      stopStream();
      setRecorderState("error");
      setErrorMessage(getReadableError(error, text));
    }
  }, [applyAudioPreview, clearPreview, isProcessing, stopStream, stopTimer, text]);

  const togglePreviewPlayback = useCallback(async () => {
    const audio = previewAudioRef.current;
    if (!audio || !previewUrl) return;

    try {
      if (isPreviewPlaying) {
        audio.pause();
        setIsPreviewPlaying(false);
      } else {
        await audio.play();
        setIsPreviewPlaying(true);
      }
    } catch (error) {
      setErrorMessage(getReadableError(error, text));
      setRecorderState("error");
    }
  }, [isPreviewPlaying, previewUrl, text]);

  const openUploadPicker = useCallback(() => {
    if (isRecording || isProcessing) return;
    fileInputRef.current?.click();
  }, [isProcessing, isRecording]);

  const handleAudioFileSelected = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const looksLikeAudio =
        String(file.type || "").startsWith("audio/") ||
        /\.(m4a|mp3|wav|ogg|webm|aac|flac)$/i.test(file.name || "");

      if (!looksLikeAudio) {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setRecorderState("error");
        setErrorMessage(text.invalidAudioFile);
        return;
      }

      await applyAudioPreview(file, "upload", file.name || text.previewFile);
    },
    [applyAudioPreview, text]
  );

  const submitPreview = useCallback(async () => {
    if (!audioBlob || isProcessing) return;

    if (!organizationId) {
      setRecorderState("error");
      setErrorMessage(text.organizationMissing);
      return;
    }

    const controller = new AbortController();
    uploadAbortRef.current = controller;
    setErrorMessage("");
    setRecorderState("processing");

    try {
      const activeSession = isSessionExpired(conversation.sessionExpiresAt)
        ? createSession()
        : {
            sessionId: isUuid(sessionId) ? sessionId : createSession().sessionId,
            sessionExpiresAt: conversation.sessionExpiresAt,
          };

      const response = await processVoiceCommand({
        audioBlob,
        sessionId: activeSession.sessionId,
        language: lang,
        organizationId,
        signal: controller.signal,
      });

      const nextSessionId = isUuid(response.sessionId) ? response.sessionId : activeSession.sessionId;
      const userMessage = {
        id: createId(),
        role: "user",
        text: response.transcript || text.voiceMessage,
        duration: recordedMs,
        createdAt: new Date().toISOString(),
      };
      const assistantMessage = {
        id: createId(),
        role: "assistant",
        text:
          response.responseText ||
          (response.needsClarification ? text.clarifyRequest : text.assistantFallback),
        audioSource: response.audioSource,
        needsClarification: response.needsClarification,
        clarification: response.clarification,
        createdAt: new Date().toISOString(),
      };

      updateConversation((prev) => ({
        sessionId: nextSessionId,
        sessionExpiresAt:
          nextSessionId === activeSession.sessionId
            ? activeSession.sessionExpiresAt
            : Date.now() + SESSION_TTL_MS,
        messages: [...prev.messages, userMessage, assistantMessage],
      }));

      clearPreview();
      setRecorderState("ready");
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return;
      setRecorderState("preview");
      setErrorMessage(getReadableError(error, text));
    } finally {
      uploadAbortRef.current = null;
    }
  }, [
    audioBlob,
    clearPreview,
    conversation.sessionExpiresAt,
    isProcessing,
    lang,
    organizationId,
    recordedMs,
    sessionId,
    text,
    updateConversation,
  ]);

  const submitChatMessage = useCallback(async () => {
    const prompt = chatInput.trim();

    if (!prompt || isChatProcessing) return;

    if (!organizationId) {
      setChatState("error");
      setErrorMessage(text.organizationMissing);
      return;
    }

    const controller = new AbortController();
    chatAbortRef.current = controller;
    setChatInput("");
    setErrorMessage("");
    setChatState("processing");

    const userMessage = {
      id: createId(),
      role: "user",
      text: prompt,
      mode: "chat",
      createdAt: new Date().toISOString(),
    };

    updateConversation((prev) => ({
      messages: [...prev.messages, userMessage],
    }));

    try {
      const response = await sendAssistantChatMessage({
        prompt,
        subscriberId: user?.subid || org?.subid,
        organizationId,
        signal: controller.signal,
      });

      const assistantMessage = {
        id: createId(),
        role: "assistant",
        text: response.text || text.chatFallback,
        sources: response.sources,
        mode: "chat",
        createdAt: new Date().toISOString(),
      };

      updateConversation((prev) => ({
        messages: [...prev.messages, assistantMessage],
      }));
      setChatState("ready");
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return;
      setChatState("error");
      setErrorMessage(getReadableError(error, text));
      setChatInput(prompt);
    } finally {
      chatAbortRef.current = null;
    }
  }, [
    chatInput,
    isChatProcessing,
    org?.subid,
    organizationId,
    text,
    updateConversation,
    user?.subid,
  ]);

  const handleChatKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter" || event.shiftKey) return;
      event.preventDefault();
      submitChatMessage();
    },
    [submitChatMessage]
  );

  const switchAssistantMode = useCallback(
    (mode) => {
      if (mode === "voice" && !canUseVoiceCommand) return;
      if (mode === "chat" && !canUseAssistantChat) return;
      if (mode === assistantMode) return;
      setAssistantMode(mode);
      setErrorMessage("");

      if (mode === "chat") {
        cancelRecording();
      }
    },
    [assistantMode, canUseAssistantChat, canUseVoiceCommand, cancelRecording]
  );

  const handlePrimaryAction = useCallback(() => {
    if (isProcessing) return;
    if (isRecording) {
      stopRecording();
      return;
    }
    if (hasPreview) {
      submitPreview();
      return;
    }
    startRecording();
  }, [hasPreview, isProcessing, isRecording, startRecording, stopRecording, submitPreview]);

  const resetConversation = useCallback(() => {
    cancelRecording();
    chatAbortRef.current?.abort();
    const next = {
      key: conversationKey,
      ...createSession(),
      messages: [],
    };

    try {
      window.sessionStorage.removeItem(conversationKey);
    } catch (error) {
      console.error("Failed to clear AI voice assistant conversation:", error);
    }

    setConversation(next);
    setErrorMessage("");
    setRecorderState("ready");
    setChatState("ready");
    setChatInput("");
  }, [cancelRecording, conversationKey]);

  useEffect(() => {
    if (!settings.enabled) {
      setOpen(false);
      cancelRecording();
    }
  }, [cancelRecording, settings.enabled]);

  useEffect(() => {
    const nextMode = availableAssistantModes[0];

    if (!nextMode) {
      return;
    }

    if (!availableAssistantModes.includes(assistantMode)) {
      if (assistantMode === "voice") {
        cancelRecording();
      }
      if (assistantMode === "chat") {
        chatAbortRef.current?.abort();
        setChatState("ready");
      }
      setAssistantMode(nextMode);
    }
  }, [assistantMode, availableAssistantModes, cancelRecording]);

  useEffect(() => {
    if (!open) return;
    messagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [messages, open]);

  useEffect(() => {
    return () => {
      uploadAbortRef.current?.abort();
      chatAbortRef.current?.abort();
      discardRecordingRef.current = true;
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
      stopTimer();
      stopStream();
      if (previewUrlRef.current) {
        window.URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, [stopStream, stopTimer]);

  if (!canUseAssistant || !settings.enabled) return null;

  const primaryTitle = isRecording
    ? text.stop
    : hasPreview
      ? text.send
      : text.start;
  const previewTitle = getPreviewTitle(previewSource, previewLabel, text);

  return (
    <div className={shellClassName} dir={isRtl ? "rtl" : "ltr"}>
      {open ? (
        <section className="ai-va-panel" aria-label={text.title}>
          <div className="ai-va-panel-header">
            <div className="ai-va-title-block">
              <span className="ai-va-title-icon" aria-hidden="true">
                {activeAssistantMode === "chat" ? (
                  <ChatBubbleOutlineRoundedIcon fontSize="small" />
                ) : (
                  <AutoAwesomeRoundedIcon fontSize="small" />
                )}
              </span>
              <div>
                <h2>{text.title}</h2>
                <span className={`ai-va-status is-${recorderState}`}>
                  <span className="ai-va-status-dot" />
                  {statusText}
                </span>
              </div>
            </div>

            <div className="ai-va-header-actions">
              <button type="button" onClick={resetConversation} title={text.clear} aria-label={text.clear}>
                <RestartAltRoundedIcon fontSize="small" />
              </button>
              <button type="button" onClick={() => setOpen(false)} title={text.close} aria-label={text.close}>
                <CloseRoundedIcon fontSize="small" />
              </button>
            </div>
          </div>

          {showModeTabs ? (
            <div className="ai-va-mode-tabs" role="tablist" aria-label={text.modeLabel}>
              <button
                type="button"
                className={activeAssistantMode === "voice" ? "is-active" : ""}
                onClick={() => switchAssistantMode("voice")}
                disabled={isChatProcessing}
                role="tab"
                aria-selected={activeAssistantMode === "voice"}
              >
                <KeyboardVoiceRoundedIcon fontSize="small" />
                <span>{text.voiceMode}</span>
              </button>
              <button
                type="button"
                className={activeAssistantMode === "chat" ? "is-active" : ""}
                onClick={() => switchAssistantMode("chat")}
                disabled={isRecording || isProcessing}
                role="tab"
                aria-selected={activeAssistantMode === "chat"}
              >
                <ChatBubbleOutlineRoundedIcon fontSize="small" />
                <span>{text.chatMode}</span>
              </button>
            </div>
          ) : null}

          <div className="ai-va-conversation">
            {messages.length === 0 ? (
              <div className="ai-va-empty">
                <div className="ai-va-empty-orb" aria-hidden="true">
                  {activeAssistantMode === "chat" ? <ChatBubbleOutlineRoundedIcon /> : <GraphicEqRoundedIcon />}
                </div>
                <h3>{activeAssistantMode === "chat" ? text.chatEmptyTitle : text.emptyTitle}</h3>
                <p>{activeAssistantMode === "chat" ? text.chatEmptySubtitle : text.emptySubtitle}</p>
              </div>
            ) : (
              messages.map((message) => <MessageBubble key={message.id} message={message} text={text} />)
            )}
            <div ref={messagesEndRef} />
          </div>

          {activeAssistantMode === "chat" ? (
            <form
              className="ai-va-composer ai-va-chat-composer"
              onSubmit={(event) => {
                event.preventDefault();
                submitChatMessage();
              }}
            >
              {errorMessage ? <div className="ai-va-error">{errorMessage}</div> : null}

              <label className="ai-va-chat-input-shell">
                <span className="ai-va-chat-input-label">{text.chatInputLabel}</span>
                <textarea
                  value={chatInput}
                  onChange={(event) => setChatInput(event.target.value)}
                  onKeyDown={handleChatKeyDown}
                  placeholder={text.chatPlaceholder}
                  rows={3}
                  disabled={isChatProcessing}
                />
              </label>

              <div className="ai-va-chat-actions">
                <span>{statusText}</span>
                <button
                  type="submit"
                  className="ai-va-chat-send"
                  disabled={!chatInput.trim() || isChatProcessing}
                  title={text.sendChat}
                  aria-label={text.sendChat}
                >
                  {isChatProcessing ? <span className="ai-va-spinner" /> : <SendRoundedIcon />}
                </button>
              </div>
            </form>
          ) : (
          <div className="ai-va-composer">
            {errorMessage && recorderState !== "recording" ? (
              <div className="ai-va-error">{errorMessage}</div>
            ) : null}

            <div className="ai-va-upload-row">
              <button
                type="button"
                className="ai-va-upload-button"
                onClick={openUploadPicker}
                disabled={isRecording || isProcessing}
                title={text.upload}
                aria-label={text.upload}
              >
                <UploadFileRoundedIcon fontSize="small" />
                <span>{text.upload}</span>
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,.m4a,.mp3,.wav,.ogg,.webm,.aac,.flac"
                className="ai-va-file-input"
                onChange={handleAudioFileSelected}
                tabIndex={-1}
                aria-hidden="true"
              />
            </div>

            <div className="ai-va-orb-wrap" aria-hidden="true">
              <div className={`ai-va-orb is-${recorderState}`}>
                <span />
                <span />
              </div>
            </div>

            <div className="ai-va-controls">
              <button
                type="button"
                className="ai-va-mini-control"
                onClick={togglePreviewPlayback}
                disabled={!hasPreview || isProcessing}
                title={isPreviewPlaying ? text.pause : text.play}
                aria-label={isPreviewPlaying ? text.pause : text.play}
              >
                {isPreviewPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
              </button>

              <button
                type="button"
                className={`ai-va-record-button is-${recorderState}`}
                onClick={handlePrimaryAction}
                disabled={isProcessing}
                title={primaryTitle}
                aria-label={primaryTitle}
              >
                {isProcessing ? (
                  <span className="ai-va-spinner" />
                ) : isRecording ? (
                  <StopRoundedIcon />
                ) : hasPreview ? (
                  <SendRoundedIcon />
                ) : (
                  <MicNoneOutlinedIcon />
                )}
              </button>

              <button
                type="button"
                className="ai-va-mini-control"
                onClick={cancelRecording}
                disabled={(!hasPreview && !isRecording) || isProcessing}
                title={text.discard}
                aria-label={text.discard}
              >
                <DeleteOutlineRoundedIcon />
              </button>
            </div>

            <div className="ai-va-composer-status">
              <span>{statusText}</span>
              {(isRecording || hasPreview) && <span>{formatDuration(recordedMs)}</span>}
            </div>

            {hasPreview && previewTitle ? (
              <div className="ai-va-preview-pill" title={previewTitle}>
                <AudioFileRoundedIcon fontSize="small" />
                <span>{previewTitle}</span>
              </div>
            ) : null}

            <audio
              ref={previewAudioRef}
              src={previewUrl}
              onEnded={() => setIsPreviewPlaying(false)}
              onPause={() => setIsPreviewPlaying(false)}
            />
          </div>
          )}
        </section>
      ) : (
        <button
          type="button"
          className="ai-va-launcher"
          onClick={() => setOpen(true)}
          title={text.open}
          aria-label={text.open}
        >
          <span className="ai-va-launcher-glow" />
          <MicNoneOutlinedIcon />
          {messages.length > 0 ? <span className="ai-va-launcher-count">{messages.length}</span> : null}
        </button>
      )}
    </div>
  );
}
